import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { FormEvent, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import FormInput from "../forms/form-input/FormInput";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";

export type SaveAsDialogInitValues = {
    name: string;
    configurationType: string;
    forceEnable?: boolean;
    maxLength?: number;
    validate?: (value: string) => boolean | string;
};

export type SaveAsDialogResultValues = {
    name: string;
};

export type SaveAsDialogResult = DialogResult<SaveAsDialogResultValues>;

export type SaveAsDialogProps = DialogProps<SaveAsDialogInitValues, SaveAsDialogResult>;

export type SaveAsFormValuesType = {
    name: string;
};

export default function SaveAsDialog({
    open,
    onClose,
    values
}: SaveAsDialogProps) {

    const { t } = useTranslation();
    const { confirm } = useConfirmation();

    const form = useForm<SaveAsFormValuesType>({
        mode: "onChange",
        defaultValues: {
            name: values?.name
        } as SaveAsFormValuesType
    });

    const {
        handleSubmit,
        getValues,
        formState: {
            isDirty,
            isValid,
        },
        reset
    } = form;


    async function onSubmit(data: SaveAsFormValuesType) {

        const dialogValues: SaveAsDialogResultValues = {
            name: data.name.trim()
        };

        reset();

        onClose && onClose({
            reason: "ok",
            values: dialogValues
        });
    }

    async function onCancel() {
        const canCancel = isDirty
            ? await getUserExitConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                reason: "cancel"
            });
        }
    }

    async function onFormSubmit(ev: FormEvent<Element>) {
        
        if ((isDirty && isValid) || values?.forceEnable) {
            const data = getValues();
            onSubmit(data);

            ev.preventDefault();
        }

    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    useEffect(() => {

        reset({
            name: values?.name
        });
    }, [
        reset,
        values
    ]);

    return (
        <>
            <ResponsiveDialog
                title={t("configuration.saveAsDialog.title")}
                enableOkay={(isDirty && isValid) || values?.forceEnable}
                okayLabel={t("configuration.saveAsDialog.buttonLabel")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onSubmit(data))()
                            : onCancel();
                    }
                }
            >
                <Form form={form}
                    onSubmit={(ev) => onFormSubmit(ev)}>
                    <FormInput
                        id="name"
                        name="name"
                        helperText={t("configuration.saveAsDialog.instructions", { configurationType: values?.configurationType ?? ""})}
                        ariaLabel={t("configuration.saveAsDialog.inputLabel", { configurationType: values?.configurationType ?? "" })}
                        label={t("configuration.saveAsDialog.inputLabel", { configurationType: values?.configurationType ?? "" })}
                        rules={{
                            required: {
                                value: true,
                                message: t("configuration.saveAsDialog.nameRequired")
                            },
                            maxLength: {
                                value: values?.maxLength ?? 64,
                                message: t("configuration.saveAsDialog.maxNameLength")
                            },
                            validate: (value: string) => values?.validate ? 
                                                            values.validate(value) : 
                                                            value.trim().length > 0 || t("configuration.saveAsDialog.nameRequired")
                        }}
                    />
                </Form>

            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserExitConfirmation}
            />
        </>
    );
}

export type SaveAsDialogState = DialogState<SaveAsDialogInitValues, SaveAsDialogResult>;

export type BindSaveAsDialogReturn = BindDialogReturn<SaveAsDialogInitValues, SaveAsDialogResult>;

export function useSaveAsDialogState(): SaveAsDialogState {
    return useDialogState();
}

export function bindSaveAsDialog(props: SaveAsDialogState): BindSaveAsDialogReturn {
    return bindDialog(props);
}
